import {Alert, Button, Checkbox, Col, Divider, Flex, Form, Input, notification, Row, Typography} from "antd";
import {useLocation, useNavigate} from "react-router";
import {useEffect, useRef, useState} from "react";
import {getRequest, postRequest} from "../../../api/httpRequest";
import dayjs from "dayjs";
import {useSearchParams} from "react-router-dom";

function formatLockTime(totalSeconds: number) {
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes}:${seconds.toString().padStart(2, "0")}`
}

export default function LoginContent() {

    const [isLogging, setIsLogging] = useState(false)
    const [api, contextHolder] = notification.useNotification()
    const navigate = useNavigate()
    const location = useLocation()
    const [form] = Form.useForm()
    const [passwordError, setPasswordError] = useState(false)
    const [loginError, setLoginError] = useState(false)
    const [searchParams, setSearchParams] = useSearchParams();
    const [lockSecondsLeft, setLockSecondsLeft] = useState(0)
    const lockIntervalRef = useRef<ReturnType<typeof setInterval>>()
    const hiddenRegistrationHosts = ["lk-abit.miit.ru", "lk.rut-miit.ru", "localhost", "lk.dev.miit.ru"];
    const isRegistrationHidden = hiddenRegistrationHosts.includes(window.location.hostname);
    const infoBlockHosts = ["lk-abit.miit.ru", "lk.dev.miit.ru"];
    const isInfoBlockVisible = infoBlockHosts.includes(window.location.hostname);


    useEffect(() => {
        const token = searchParams.get("token")
        if (token) {
            navigate("/login/resetPass", {state: {token: token}})
        }
    }, []);

    useEffect(() => {
        return () => clearInterval(lockIntervalRef.current)
    }, []);

    function startLockCountdown(seconds: number) {
        clearInterval(lockIntervalRef.current)
        setLockSecondsLeft(seconds)
        lockIntervalRef.current = setInterval(() => {
            setLockSecondsLeft(prev => {
                if (prev <= 1) {
                    clearInterval(lockIntervalRef.current)
                    return 0
                }
                return prev - 1
            })
        }, 1000)
    }

    const esiaLogin = () => {
        getRequest("/esia/sign/auth")
            .then(response => {
                window.location.replace(response.data.url)
            })
            .catch(error => {
                console.error(error)
            })
    }

    async function digestMessage(message: string) {
        const CryptoJS = require("crypto-js");
        const hash = CryptoJS.SHA1(message);
        return hash.toString(CryptoJS.enc.Hex)
    }

    async function handleLogin(values: any) {
        if (lockSecondsLeft > 0) {
            return
        }
        dayjs().locale('ru')
        setIsLogging(true)
        let shaHashPassword = await digestMessage(values.password);
        postRequest("/server/api/auth/login", {
            login: values.username,
            password: shaHashPassword
        }).then(response => {
            setIsLogging(false)
            if (response.status === 401) {
                setPasswordError(true)
            }
            navigate("/")
        }).catch(error => {
            console.log(JSON.stringify(error, null, 2));
            if (error.response.status === 429) {
                const retryAfter = parseInt(error.response.headers?.["retry-after"], 10)
                startLockCountdown(Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 300)
                setPasswordError(true)
            } else {
                api.error({
                    message: `Ошибка авторизации`,
                    description: error.response.data.message,
                    duration: 3
                });
                if (error.response.status === 401) {
                    setPasswordError(true)
                }
                if (error.response.status === 400) {
                    setLoginError(true)
                }
            }
            setIsLogging(false)
        })
    }

    function loginForm() {
        return <Form
            name="login"
            form={form}
            labelCol={{flex: '75px'}}
            labelAlign="left"
            initialValues={{remember: true}}
            onFinish={values => handleLogin(values)}
            autoComplete="off"
            requiredMark={false}>
            {lockSecondsLeft > 0 && (
                <Form.Item wrapperCol={{offset: 0, span: 24}}>
                    <Alert
                        type="error"
                        showIcon
                        message={`Слишком много неудачных попыток входа. Повторите через ${formatLockTime(lockSecondsLeft)}`}/>
                </Form.Item>
            )}
            <Form.Item
                label="Логин"
                name="username"
                rules={[{required: true, message: 'Логин не может быть пустым!'}]}>
                <Input
                    disabled={lockSecondsLeft > 0}
                    status={loginError ? "error" : undefined}/>
            </Form.Item>

            <Form.Item
                label="Пароль"
                name="password"
                rules={[{required: true, message: 'Пароль не может быть пустым!'}]}
            >
                <Input.Password
                    disabled={lockSecondsLeft > 0}
                    status={passwordError ? "error" : undefined}/>
            </Form.Item>
            <Flex gap={16} align={"center"}>
                <Row gutter={16}>
                    <Col>
                        <Form.Item>
                            <Button loading={isLogging}
                                    disabled={lockSecondsLeft > 0}
                                    style={{width: "150px", height: "38.5px"}}
                                    type="primary"
                                    htmlType="submit">
                                Войти
                            </Button>
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item wrapperCol={{flex: "200px"}} name="remember" valuePropName="checked">
                            <Checkbox>Запомнить меня</Checkbox>
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item wrapperCol={{flex: "200px"}} name="forget" valuePropName="checked">
                            <Button type={"text"} style={{color: "blue"}} onClick={() => navigate("/login/restore")}>Забыли
                                пароль?</Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Flex>
        </Form>;
    }

    return (
        <Flex className={"content content-xs"} vertical={true}>
            <Flex vertical={true}>
                {contextHolder}
                <Flex vertical gap={"16px"}>
                    <div className={"lkText"}>Личный кабинет</div>
                    <div className={"continueText"}>Для продолжения, пожалуйста, введите логин и пароль</div>
                </Flex>
                <Divider style={{margin: 16}}/>
                {loginForm()}
                {isInfoBlockVisible && (
                    <Flex
                        vertical
                        gap={8}
                        style={{background: "#f5f7ff", border: "1px solid #d6ddff", borderRadius: "8px", padding: "12px 16px"}}
                    >
                        <Typography.Text>
                            Логин и пароль для личного кабинета отправляются автоматически после подачи документов. Данные поступят на почту, указанную в заявлении. Обычно это занимает некоторое время.
                        </Typography.Text>
                        <Typography.Text>Если письма нет:</Typography.Text>
                        <ul style={{margin: 0, paddingLeft: "20px"}}>
                            <li>Убедитесь, что используете правильный email (как в заявлении)</li>
                            <li>Проверьте папку Спам</li>
                            <li>Воспользуйтесь ссылкой Забыли пароль выше, логин - почта, указанная при поступлении</li>
                        </ul>
                    </Flex>
                )}
                {!isRegistrationHidden && (
                    <Flex align={"center"}>
                        <Typography.Text>Нет учетной записи?</Typography.Text>
                        <Button type={"text"} style={{color: "blue"}}
                                onClick={() => navigate("/login/registration")}>Зарегистрироваться</Button>
                    </Flex>
                )}
            </Flex>
        </Flex>
    )
}
